import type { MapDocument } from "../document/types.ts";
import { areaGridScale, areaGridSize } from "./area.ts";
import type { AreaReplaced } from "./area-move.ts";

/**
 * The painted destination cells a move replaces (ADR-128 decisions 19 and 20), as boxes in tiles for the map's tint:
 * a tile cell a tile, a quarter cell a quarter of one each way, a corner a half-tile box on its vertex; cells side
 * by side on a row of a tile or quarter grid are one box.
 */

/**
 * A box on the map in tiles, fractions of one included.
 */
export interface AreaTileBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const CORNER_BOX = 0.5;

/**
 * The replaced paint as boxes in tiles.
 *
 * @param map The map the plan was made against.
 * @param replaced The plan's replaced paint, each layer's cells in index order.
 * @returns The boxes, layer by layer, in index order.
 */
export function areaReplacedBoxes(map: MapDocument, replaced: readonly AreaReplaced[]): AreaTileBox[] {
  const boxes: AreaTileBox[] = [];
  for (const entry of replaced) {
    const { width } = areaGridSize(map, entry.grid);
    const size = 1 / areaGridScale(entry.grid);
    if (entry.grid === "corners") {
      for (const index of entry.indices) {
        boxes.push({ x: (index % width) - CORNER_BOX / 2, y: Math.floor(index / width) - CORNER_BOX / 2, w: CORNER_BOX, h: CORNER_BOX });
      }
      continue;
    }
    let first = -1;
    let last = -1;
    /**
     * Ends the run being gathered as one box.
     */
    const flush = (): void => {
      if (first >= 0) {
        boxes.push({ x: (first % width) * size, y: Math.floor(first / width) * size, w: (last - first + 1) * size, h: size });
      }
    };
    for (const index of entry.indices) {
      if (first >= 0 && index === last + 1 && index % width !== 0) {
        last = index;
      } else {
        flush();
        first = index;
        last = index;
      }
    }
    flush();
  }
  return boxes;
}
